import { IconV1 } from 'common/icon/iconV1.js';
import { LinkFactoryReactRouter } from 'common/link/link-react-router.js';
import { RenderNotes } from 'common/render-notes.js';
import { Tabs } from 'common/tabs/tab.js';
import { TileBodyHeaderV2 } from 'common/tile/tile-body-header-v2.js';
import { Tile } from 'common/tile/tile.js';
import { IModule } from 'redux-state/layout/types.js';
import { IModuleConfig } from 'redux-state/layout/types.modules.js';

enum VALUE_DECORATIONS {
  Hottest = '°',
  Coldest = '°',
  Wettest = 'mm',
  Windiest = 'km/h',
}

interface IExtremeTab {
  name: keyof typeof VALUE_DECORATIONS;
  description: string;
  headers: string[];
  values: Array<IExtremeRow>;
  keys: string[];
}

enum EXTREME_ROW_KEYS {
  LOCATION = 'location',
  RANK = 'rank',
  UPDOWN = 'upDown',
  VALUE = 'value',
  TOWN = 'town',
}

interface IExtremeRow {
  location: string;
  rank: number;
  prev: number;
  upDown: string;
  value: string;
  town: string;
  [key: string]: string | number;
}

interface IYesterdaysExtremes extends IModuleConfig {
  tabs: IExtremeTab[];
}
export const YesterdaysExtremes = (props: IModule<IYesterdaysExtremes>) => {
  const {
    config: { tabs },
  } = props;

  const getRankDiffs = (row: IExtremeRow) => {
    let diff;
    let didIncrease;
    let didDecrease;

    if (row.prev - row.rank !== 0) {
      if (row.prev - row.rank > 0) {
        didIncrease = true;
        diff = `+${row.prev - row.rank}`;
      } else {
        didDecrease = true;
        diff = `${row.prev - row.rank}`;
      }
    }

    return {
      didIncrease,
      didDecrease,
      diff,
    };
  };

  const renderRowData = (key: string, row: IExtremeRow, dIdx: number, name: keyof typeof VALUE_DECORATIONS) => {
    const isLocationKey = key === EXTREME_ROW_KEYS.LOCATION;
    const isUpDownKey = key === EXTREME_ROW_KEYS.UPDOWN;
    const isValueKey = key === EXTREME_ROW_KEYS.VALUE;
    const { didIncrease, didDecrease, diff } = getRankDiffs(row);

    if (dIdx <= 1) {
      if (isLocationKey) {
        return (
          <LinkFactoryReactRouter className="ExtremesTable-title" href={row.town}>
            {row[key]}
          </LinkFactoryReactRouter>
        );
      }
      return (
        <b className="small u-flex u-flexAlignItemsCenter" key={dIdx} style={{ lineHeight: 1 }}>
          <>
            {didIncrease && <IconV1 icon="arrow-up" iconClass="svg--1em svg-color-link" />}
            {didDecrease && <IconV1 icon="arrow-down" iconClass="svg--1em svg-color-link" />}
            {row[key]}
          </>
        </b>
      );
    }

    return (
      <span className="small u-inlineBlock" key={dIdx}>
        {isUpDownKey ? (
          <b className="ExtremesTable-diff">{diff}</b>
        ) : (
          <>
            {row[key]}
            {isValueKey && <>{VALUE_DECORATIONS[name]}</>}
          </>
        )}
      </span>
    );
  };

  return (
    <Tile dataModule="yesterdays-extremes" {...props}>
      <div className="Tile-body">
        <TileBodyHeaderV2 isDrawer={props.isDrawer} modulePath={props.modulePath} config={props.config} />
        <Tabs
          headers={tabs.map(({ name }) => name)}
          renderActiveTab={(activeTabIndex) => {
            const activeTab = tabs[activeTabIndex];
            const { description, headers, values, name, keys } = activeTab;

            if (!keys?.length) {
              return null;
            }

            return (
              <>
                <div className="ExtremesTable">
                  <div className="ExtremesTable-wrapper">
                    <table className="ExtremesTable-table">
                      <thead className="ExtremesTable-head">
                        <tr className="ExtremesTable-table-row">
                          {headers.map((header, idx) => (
                            <th key={idx} className="h4">
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="ExtremesTable-body">
                        {values.map((row, rowIdx) => {
                          return (
                            <tr className="ExtremesTable-table-row" key={rowIdx}>
                              {keys.map((key, dIdx) => {
                                if (dIdx <= 1) {
                                  return (
                                    <th key={dIdx} className="small">
                                      {renderRowData(key, row, dIdx, name)}
                                    </th>
                                  );
                                }

                                return (
                                  <td key={dIdx} className="small">
                                    {renderRowData(key, row, dIdx, name)}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
                {description && (
                  <div className="ExtremeTable-description">
                    <RenderNotes classes="u-mB-0" notes={[{ text: description }]} />
                  </div>
                )}
              </>
            );
          }}
        />
      </div>
    </Tile>
  );
};
